import {
  Box,
  CloseIcon,
  ContentRow,
  Dialog,
  Space,
  useIsMobileLayout,
} from "@dbn/omnichannel-design-system-g4p";
import { boolean, option, readerTaskEither, taskEither } from "fp-ts";
import { constant, constVoid, flow, pipe } from "fp-ts/function";
import {
  ExistingClientAuthenticationMethod,
  genericError,
} from "../../globalDomain";
import { useCommand } from "../../useAPI";
import { IdUpload } from "../../IdUpload/IdUpload";
import * as api from "../api";
import { useClientExistencyCheck } from "../../UploadDocuments/clientExistenceUtils";
import * as uploadApi from "../../IdUpload/api";
import { BackButton } from "../../Common/BackButton/BackButton";
import { IO } from "fp-ts/IO";
import { RefuseExistingClientReason, StandardLoanFlowType } from "../domain";
import { Option } from "fp-ts/Option";
import { useFlowTypeCases } from "../useFlowTypeCases";
import { useState } from "react";
import { RuntimeLocaleKey, useFormatMessage } from "../../intl";
import { useIsInPersonChannel } from "../../useChannel";
import { useAppContext } from "../../useAppContext";

type Props = {
  onComplete: () => unknown;
  onRefuse: (reason: RefuseExistingClientReason) => void;
  onFailure: (
    reason: "GenericError" | "MaxAttemptsReached" | "ValidationError"
  ) => unknown;
  onBack: IO<unknown>;
  authenticationMethod: Option<ExistingClientAuthenticationMethod>;
  flowType: StandardLoanFlowType;
  isThirdParty: boolean;
};

export function UploadId(props: Props) {
  const {
    config: { enableThirdPartyBranchSelection },
  } = useAppContext();
  const clientExistenceCommand = useCommand(api.checkClientExisting);
  const formatMessage = useFormatMessage();
  const [isBelow18, setIsBelow18] = useState(false);
  const clientExistenceCheck = useClientExistencyCheck(
    clientExistenceCommand,
    () => genericError
  );
  const openClient = useCommand(api.openClient);

  const personalInfoSubmit = useCommand(api.personalInfoSubmit);
  const editData = useCommand(uploadApi.personalInfoEdit);
  const isMobileLayout = useIsMobileLayout();

  const {
    isPWSRemote,
    isTLS,
    isTLSorPWSRemoteAndCZ,
    isTLSorPWSRemoteAndSK,
  } = useFlowTypeCases(props.flowType);
  const isInPersonChannel = useIsInPersonChannel();
  const isTLSorPWSremoteExistingClient =
    (isPWSRemote || isTLS) && option.isSome(props.authenticationMethod);

  const documentIdentificationFlow = isTLSorPWSremoteExistingClient
    ? "Primary"
    : isTLSorPWSRemoteAndCZ
    ? "PrimaryAndSecondary"
    : isTLSorPWSRemoteAndSK
    ? "PrimaryAndLivenessCheck"
    : "Primary";

  const extractData = useCommand(api.extractClientData)({
    idType:
      documentIdentificationFlow === "PrimaryAndSecondary"
        ? option.none
        : option.some("Primary"),
    coApplicant: option.none,
  });

  const rejectData = useCommand(uploadApi.rejectData)({
    idType:
      documentIdentificationFlow === "PrimaryAndSecondary"
        ? option.none
        : option.some("Primary"),
    coApplicant: option.none,
  });

  const checkBelow18 = (dateOfBirth: Date) => {
    const age = new Date().getFullYear() - dateOfBirth.getFullYear();

    return age < 18;
  };

  return (
    <Box column grow shrink>
      <IdUpload
        productType={option.some("SL")}
        coApplicant={option.none}
        onComplete={flow(
          taskEither.fromOption(constant(genericError)),
          taskEither.chain(clientExistenceCheck),
          taskEither.mapLeft(() => genericError),
          taskEither.chain(clientData => {
            if (clientData.hasBlockingNotes) {
              return taskEither.fromIO(() =>
                props.onRefuse("HasBlockingNotes")
              );
            }
            return pipe(
              clientData.clientExistencyCheckData.dateOfBirth,
              checkBelow18,
              boolean.fold(
                () =>
                  !clientData.clientExists && clientData.personalNumberMatch
                    ? taskEither.fromIO(() =>
                        props.onRefuse("IsPersonalNumberMatch")
                      )
                    : pipe(
                        clientData.clientExists,
                        boolean.fold(
                          () =>
                            pipe(
                              openClient(),
                              taskEither.mapLeft(constant(genericError)),
                              taskEither.chain(() =>
                                taskEither.fromIO(() => props.onComplete())
                              )
                            ),
                          () =>
                            taskEither.fromIO(() => {
                              if (props.isThirdParty) {
                                return props.onComplete();
                              }
                              return pipe(
                                clientData.userID,
                                option.fold(
                                  () =>
                                    props.onRefuse(
                                      clientData.multipleUsers
                                        ? "IsMultipleUser"
                                        : "IsExisting"
                                    ),
                                  () => props.onRefuse("HasUserId")
                                )
                              );
                            })
                        )
                      ),
                () => taskEither.fromIO(() => setIsBelow18(true))
              )
            );
          })
        )}
        onFailure={props.onFailure}
        onShouldClientContinue={option.none}
        documentIdentificationFlow={documentIdentificationFlow}
        extractData={extractData}
        clientExistencyCommand={clientExistenceCommand}
        rejectData={rejectData}
        submitConfirmedDataCommand={confirmedData =>
          pipe({ idType: confirmedData.idType }, personalInfoSubmit)
        }
        documentsMismatch={false}
        onMismatchDialogDismiss={constVoid}
        showMismatchDialog={constVoid}
        onReset={constVoid}
        isThirdParty={props.isThirdParty}
        checkOpuAndCex={!props.isThirdParty && !isTLS && isInPersonChannel}
        editPersonalInfoCommand={editData}
        submitResidencyCommand={readerTaskEither.fromIO(constVoid)}
        showBranchSelection={
          props.isThirdParty && enableThirdPartyBranchSelection
        }
      />
      <Space units={4} />
      <ContentRow type="full">
        <Box grow column={isMobileLayout}>
          <BackButton action={props.onBack} />
        </Box>
      </ContentRow>
      {isBelow18 && (
        <Dialog
          ariaLabelMessageForCloseIcon={formatMessage(
            "Modal.closeButton" as RuntimeLocaleKey
          )}
          variant="center"
          size="medium"
          onDismiss={option.none}
          icon={CloseIcon}
          title={formatMessage("StandardLoan.UploadDocuments.below18.title")}
          subtitle={formatMessage(
            "StandardLoan.UploadDocuments.below18.description"
          )}
          actions={[
            {
              action: props.onBack,
              label: formatMessage(
                "StandardLoan.UploadDocuments.below18.close"
              ),
              variant: "secondary",
            },
          ]}
        />
      )}
    </Box>
  );
}
